/**
 * Position Event System
 * Central event bus for all position-related updates
 */

import type { MonitorStatusRecord } from "@/lib/monitoring/monitor-status.types";
import type { EnhancedPosition } from "@/types/position.types";

export type EventSource =
  | "api"
  | "websocket"
  | "user_action"
  | "monitoring"
  | "system";

export interface GenericEvent<
  TType extends string = string,
  TData = unknown,
  TSource extends string = EventSource,
> {
  type: TType;
  symbol: string;
  data: TData;
  timestamp: Date;
  source: TSource;
}

export type PositionUpdateEvent = GenericEvent<
  "POSITION_UPDATED",
  EnhancedPosition[],
  "api" | "websocket" | "user_action" | "monitoring"
>;

export type PositionClosedEvent = GenericEvent<
  "POSITION_CLOSED",
  EnhancedPosition | EnhancedPosition[] | null,
  "api" | "websocket" | "user_action" | "monitoring"
>;

export type PositionEvent = PositionUpdateEvent | PositionClosedEvent;

export type MarketDataEvent = GenericEvent<
  "MARKET_DATA_UPDATED",
  {
    bidPrice?: number;
    askPrice?: number;
    lastPrice?: number;
    midPrice?: number;
    volume?: number;
  },
  "websocket" | "api"
>;

export type GreeksEvent = GenericEvent<
  "GREEKS_UPDATED",
  {
    delta?: number;
    gamma?: number;
    theta?: number;
    vega?: number;
    impliedVolatility?: number;
  },
  "websocket" | "api"
>;

export type MonitorStatusEvent = GenericEvent<
  "MONITOR_STATUS_UPDATED",
  MonitorStatusRecord,
  "monitoring"
>;

export type KnownPositionEvents =
  | PositionEvent
  | MarketDataEvent
  | GreeksEvent
  | MonitorStatusEvent;

export type EventCallback<TEvent extends GenericEvent = GenericEvent> = (event: TEvent) => void;

export class PositionEventBus {
  private static instance: PositionEventBus;
  private subscribers = new Map<string, EventCallback[]>();
  private debugMode = false; // Disabled to reduce console noise

  static getInstance(): PositionEventBus {
    if (!PositionEventBus.instance) {
      PositionEventBus.instance = new PositionEventBus();
    }
    return PositionEventBus.instance;
  }

  /**
   * Subscribe to position events
   */
  subscribe<
    TEvent extends GenericEvent,
  >(eventType: TEvent["type"], callback: EventCallback<TEvent>): () => void {
    if (!this.subscribers.has(eventType)) {
      this.subscribers.set(eventType, []);
    }

    this.subscribers.get(eventType)!.push(callback as EventCallback);

    if (this.debugMode) {
      console.log(`[PositionEventBus] Subscribed to ${eventType}. Total subscribers: ${this.subscribers.get(eventType)!.length}`);
    }

    // Return unsubscribe function
    return () => this.unsubscribe(eventType, callback);
  }

  /**
   * Unsubscribe from events
   */
  unsubscribe<TEvent extends GenericEvent>(eventType: TEvent["type"], callback: EventCallback<TEvent>): void {
    const callbacks = this.subscribers.get(eventType) || [];
    const index = callbacks.indexOf(callback as EventCallback);

    if (index > -1) {
      callbacks.splice(index, 1);
      if (this.debugMode) {
        console.log(`[PositionEventBus] Unsubscribed from ${eventType}. Remaining subscribers: ${callbacks.length}`);
      }
    }
  }

  /**
   * Publish events to all subscribers
   */
  publish<TEvent extends GenericEvent>(event: TEvent): void {
    const callbacks = this.subscribers.get(event.type) || [];

    if (this.debugMode && callbacks.length > 0) {
      console.log(`[PositionEventBus] Publishing ${event.type} to ${callbacks.length} subscribers`, event);
    }

    // Execute all callbacks asynchronously to prevent blocking
    callbacks.forEach(callback => {
      try {
        const typedCallback = callback as EventCallback<TEvent>;
        setTimeout(() => typedCallback(event), 0);
      } catch (error) {
        console.error(`[PositionEventBus] Error in event callback for ${event.type}:`, error);
      }
    });
  }

  /**
   * Get subscriber count for debugging
   */
  getSubscriberCount(eventType?: string): number | Record<string, number> {
    if (eventType) {
      return this.subscribers.get(eventType)?.length || 0;
    }
    
    const counts: Record<string, number> = {};
    for (const [type, callbacks] of this.subscribers.entries()) {
      counts[type] = callbacks.length;
    }
    return counts;
  }

  /**
   * Clear all subscribers (useful for testing)
   */
  clearAllSubscribers(): void {
    this.subscribers.clear();
    if (this.debugMode) {
      console.log('[PositionEventBus] All subscribers cleared');
    }
  }
}

// Export singleton instance
export const positionEventBus = PositionEventBus.getInstance();
